import { useEffect, useState } from "react";
import {
  CloudDownload, ShieldCheck, KeyRound, Trash2, RefreshCcw, Loader2, X,
  CheckCircle2, AlertTriangle, Link2, Unlink, FileDown, Search, Clock,
  FileCheck2, FileQuestion, Landmark, Zap,
} from "lucide-react";
import api, { fmtMXN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";

const REQ_STATUS = {
  requested: { label: "Solicitada", color: "#22d3ee" },
  processing: { label: "En proceso SAT", color: "#fbbf24" },
  ready: { label: "Lista", color: "#22c55e" },
  downloaded: { label: "Descargada", color: "#22c55e" },
  error: { label: "Error", color: "#ef4444" },
  rejected: { label: "Rechazada", color: "#ef4444" },
  expired: { label: "Vencida", color: "#94a3b8" },
};

const MATCH_STATUS = {
  matched: { label: "Vinculada", color: "#22c55e", icon: FileCheck2 },
  suggested: { label: "Sugerencia", color: "#fbbf24", icon: FileQuestion },
  unmatched: { label: "Sin match", color: "#94a3b8", icon: FileQuestion },
  not_applicable: { label: "No aplica", color: "#64748b", icon: FileQuestion },
};

export default function SatSync() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState({ items: [], kpis: null });
  const [requests, setRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMatch, setFilterMatch] = useState("");
  const [linkModal, setLinkModal] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [st, inv, reqs, sugs] = await Promise.all([
        api.get("/sat/status"),
        api.get("/sat/invoices"),
        api.get("/sat/requests"),
        api.get("/sat/suggestions"),
      ]);
      setStatus(st.data);
      setInvoices(inv.data);
      setRequests(reqs.data);
      setSuggestions(sugs.data);
    } catch (e) {
      toast.error("No se pudo cargar el módulo SAT");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); api.get("/vendor-alerts").then(({ data }) => setAlerts(data)).catch(() => {}); }, []);

  const checkRequest = async (id) => {
    try {
      const { data } = await api.post(`/sat/requests/${id}/check`);
      const st = REQ_STATUS[data.status]?.label || data.status;
      toast.success(`Estado SAT: ${st}${data.status === "downloaded" ? ` · ${data.invoices_imported} facturas importadas` : ""}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al verificar con el SAT");
    }
  };

  const toggleAutoSync = async () => {
    try {
      await api.patch("/sat/auto-sync", { enabled: !status.auto_sync });
      setStatus({ ...status, auto_sync: !status.auto_sync });
      toast.success(!status.auto_sync ? "Sincronización diaria activada (7 AM)" : "Sincronización diaria desactivada");
    } catch {
      toast.error("Error al cambiar la configuración");
    }
  };

  const items = invoices.items.filter((it) => {
    if (filterMatch && it.match_status !== filterMatch) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (it.nombre_emisor || "").toLowerCase().includes(q)
      || (it.rfc_emisor || "").toLowerCase().includes(q)
      || (it.uuid || "").toLowerCase().includes(q);
  });

  const k = invoices.kpis;

  return (
    <div data-testid="sat-sync-page">
      <PageHeader
        subtitle="Descarga masiva de CFDIs recibidos directamente del SAT con tu e.firma"
        title="Facturas SAT"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={loadAll} data-testid="sat-refresh-btn" className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
              <RefreshCcw className="w-4 h-4" /> Actualizar
            </button>
            {status?.configured && (
              <button onClick={() => setShowSync(true)} data-testid="sat-sync-btn" className="btn-primary px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
                <CloudDownload className="w-4 h-4" /> Sincronizar
              </button>
            )}
          </div>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5">
        {alerts && (alerts.faltantes.length > 0 || alerts.duplicadas.length > 0 || alerts.discrepancias.length > 0) && (
          <div className="panel p-4" style={{ borderColor: "rgba(252,165,165,0.35)" }} data-testid="sat-alerts-panel">
            <div className="label-mono mb-3" style={{ color: "#fca5a5" }}>// ALERTAS DE COMPROBANTES</div>
            <div className="space-y-2 text-xs">
              {alerts.faltantes.length > 0 && (
                <div data-testid="sat-alerts-missing">
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#f59e0b" }}>Facturas faltantes ({alerts.faltantes.length}) — gastos con +15 días sin CFDI</div>
                  {alerts.faltantes.slice(0, 8).map((f) => (
                    <div key={f.id} className="text-slate-300">· {f.vendor || "—"} — {(f.description || "").slice(0, 50)} — <span className="font-mono">${(f.amount || 0).toLocaleString("es-MX")}</span> — <span style={{ color: "#f59e0b" }}>{f.days} días</span></div>
                  ))}
                  {alerts.faltantes.length > 8 && <div className="text-slate-500">…y {alerts.faltantes.length - 8} más</div>}
                </div>
              )}
              {alerts.duplicadas.length > 0 && (
                <div data-testid="sat-alerts-duplicates">
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#fca5a5" }}>Posibles duplicadas ({alerts.duplicadas.length})</div>
                  {alerts.duplicadas.map((d, i) => (
                    <div key={i} className="text-slate-300">· {d.emisor} — <span className="font-mono">${(d.total || 0).toLocaleString("es-MX")}</span> — {d.reason}</div>
                  ))}
                </div>
              )}
              {alerts.discrepancias.length > 0 && (
                <div data-testid="sat-alerts-mismatch">
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#c4b5fd" }}>Discrepancias CFDI vs gasto ({alerts.discrepancias.length})</div>
                  {alerts.discrepancias.map((d, i) => (
                    <div key={i} className="text-slate-300">· {d.emisor} — CFDI <span className="font-mono">${d.cfdi_total}</span> vs gasto <span className="font-mono">${d.expense_amount}</span> (dif <span className="font-mono" style={{ color: "#fca5a5" }}>${d.diff}</span>)</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {loading ? (
          <div className="panel p-10 text-center text-slate-400">Cargando…</div>
        ) : !status?.configured ? (
          <div className="panel p-10 text-center max-w-lg mx-auto" data-testid="sat-not-configured">
            <Landmark className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--cyan)" }} />
            <h3 className="font-display font-bold text-lg text-slate-100">Conecta tu e.firma (FIEL)</h3>
            <p className="text-xs text-slate-400 mt-2 max-w-md mx-auto">
              Sube tu certificado (.cer), llave privada (.key) y contraseña para descargar automáticamente
              las facturas recibidas de tu RFC. Se guardan <b>cifradas con AES</b> y solo el Admin puede configurarlas.
            </p>
            {isAdmin ? (
              <button onClick={() => setShowConfig(true)} data-testid="sat-configure-btn" className="btn-primary px-5 py-2.5 rounded-sm text-xs uppercase tracking-widest mt-5 inline-flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Configurar e.firma
              </button>
            ) : (
              <p className="text-[11px] text-slate-500 mt-4">Solicita al administrador que configure la e.firma.</p>
            )}
          </div>
        ) : (
          <>
            {/* Status card */}
            <div className="panel p-4 flex flex-wrap items-center gap-4" data-testid="sat-status-card">
              <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                <div className="p-2.5 rounded-sm" style={{ background: "rgba(34,211,238,0.12)" }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: "var(--cyan)" }} />
                </div>
                <div>
                  <div className="font-mono font-bold text-slate-100">{status.rfc}</div>
                  <div className="text-[11px] text-slate-400">{status.legal_name}</div>
                  <div className="text-[10px] text-slate-500">
                    e.firma vigente hasta {status.cert_valid_to?.slice(0, 10)}
                    {status.last_sync_at && <> · última sync {status.last_sync_at.slice(0, 16).replace("T", " ")}</>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleAutoSync}
                  data-testid="sat-autosync-toggle"
                  className="px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-2"
                  style={{
                    background: status.auto_sync ? "rgba(34,197,94,0.12)" : "rgba(15,23,42,0.6)",
                    color: status.auto_sync ? "#22c55e" : "#94a3b8",
                    border: `1px solid ${status.auto_sync ? "rgba(34,197,94,0.4)" : "var(--border)"}`,
                  }}
                >
                  <Zap className="w-3.5 h-3.5" /> Sync diaria {status.auto_sync ? "ON" : "OFF"}
                </button>
                {isAdmin && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("¿Eliminar la e.firma del sistema? Se detendrá la sincronización.")) return;
                      try { await api.delete("/sat/efirma"); toast.success("e.firma eliminada"); loadAll(); }
                      catch { toast.error("Error al eliminar"); }
                    }}
                    data-testid="sat-delete-efirma"
                    className="btn-ghost px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-2"
                    style={{ color: "#fca5a5" }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                )}
              </div>
            </div>

            {/* KPIs */}
            {k && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sat-kpis">
                <Kpi label="CFDIs descargados" value={k.total} icon={CloudDownload} color="var(--cyan)" />
                <Kpi label="Vinculadas a gastos" value={k.matched} icon={FileCheck2} color="#22c55e" />
                <Kpi label="Sugerencias por revisar" value={k.suggested} icon={FileQuestion} color="#fbbf24" />
                <Kpi label="Σ Total facturado" value={fmtMXN(k.total_amount)} icon={Landmark} color="var(--magenta)" />
              </div>
            )}

            {/* Solicitudes activas */}
            {requests.length > 0 && (
              <div className="panel p-4" data-testid="sat-requests-panel">
                <div className="label-mono mb-3">// SOLICITUDES DE DESCARGA</div>
                <div className="space-y-2">
                  {requests.slice(0, 5).map((r) => {
                    const st = REQ_STATUS[r.status] || REQ_STATUS.processing;
                    return (
                      <div key={r.id} className="flex items-center gap-3 text-xs flex-wrap" data-testid={`sat-request-${r.id}`}>
                        <span className="px-2 py-0.5 rounded-sm font-semibold text-[10px] uppercase" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}55` }}>
                          {st.label}
                        </span>
                        <span className="font-mono text-slate-300">{r.date_start} → {r.date_end}</span>
                        {r.status === "downloaded" && (
                          <span className="text-slate-400">{r.invoices_imported} importadas · {r.auto_matched} auto-vinculadas · {r.suggestions} sugerencias</span>
                        )}
                        {r.error && <span className="text-red-400 text-[11px]">{r.error}</span>}
                        {["requested", "processing"].includes(r.status) && (
                          <button onClick={() => checkRequest(r.id)} data-testid={`sat-check-${r.id}`} className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Verificar ahora
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500 mt-3">El SAT procesa las solicitudes de forma asíncrona (minutos a horas). El sistema verifica automáticamente cada 15 minutos.</p>
              </div>
            )}

            {/* Sugerencias */}
            {suggestions.length > 0 && (
              <div className="panel p-4" style={{ borderColor: "rgba(251,191,36,0.35)" }} data-testid="sat-suggestions-panel">
                <div className="label-mono mb-3" style={{ color: "#fbbf24" }}>// SUGERENCIAS DE VINCULACIÓN ({suggestions.length})</div>
                <div className="space-y-3">
                  {suggestions.map((s) => (
                    <SuggestionRow key={s.id} inv={s} onLinked={loadAll} />
                  ))}
                </div>
              </div>
            )}

            {/* Filtros + tabla */}
            <div className="panel p-3 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  data-testid="sat-search-input"
                  placeholder="Buscar emisor, RFC o UUID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-cyber pl-10 pr-3 py-2 text-sm w-full"
                />
              </div>
              {["", "matched", "suggested", "unmatched"].map((s) => (
                <button
                  key={s || "all"}
                  data-testid={`sat-filter-${s || "all"}`}
                  onClick={() => setFilterMatch(s)}
                  className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest rounded-sm"
                  style={{
                    background: filterMatch === s ? "rgba(34,211,238,0.15)" : "rgba(15,23,42,0.6)",
                    color: filterMatch === s ? "var(--cyan)" : "#94a3b8",
                    border: filterMatch === s ? "1px solid var(--cyan)" : "1px solid var(--border)",
                  }}
                >
                  {s === "" ? "Todas" : MATCH_STATUS[s].label}
                </button>
              ))}
            </div>

            {items.length === 0 ? (
              <div className="panel p-10 text-center text-slate-400" data-testid="sat-empty">
                <CloudDownload className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                <div className="text-sm">Aún no hay facturas descargadas. Presiona <b>Sincronizar</b> para pedirlas al SAT.</div>
              </div>
            ) : (
              <div className="panel overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="sat-invoices-table">
                    <thead style={{ background: "rgba(15,23,42,0.8)" }}>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="px-3 py-2.5">Fecha</th>
                        <th className="px-3 py-2.5">Emisor</th>
                        <th className="px-3 py-2.5">UUID</th>
                        <th className="px-3 py-2.5 text-right">Total</th>
                        <th className="px-3 py-2.5 text-center">Estado</th>
                        <th className="px-3 py-2.5 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                      {items.map((it) => {
                        const m = MATCH_STATUS[it.match_status] || MATCH_STATUS.unmatched;
                        return (
                          <tr key={it.id} className="hover:bg-cyan-500/5" data-testid={`sat-inv-${it.id}`}>
                            <td className="px-3 py-2.5 font-mono text-xs text-slate-300">{(it.fecha || "").slice(0, 10)}</td>
                            <td className="px-3 py-2.5">
                              <div className="text-slate-200 text-xs max-w-[220px] truncate">{it.nombre_emisor}</div>
                              <div className="text-[10px] font-mono text-slate-500">{it.rfc_emisor}</div>
                            </td>
                            <td className="px-3 py-2.5 font-mono text-[10px] text-slate-400 max-w-[140px] truncate" title={it.uuid}>{it.uuid}</td>
                            <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-100 tabular-nums">{fmtMXN(it.total)}</td>
                            <td className="px-3 py-2.5 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-semibold" style={{ background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}44` }}>
                                <m.icon className="w-3 h-3" /> {m.label}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-center gap-1">
                                {it.match_status === "matched" ? (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm("¿Desvincular esta factura del gasto?")) return;
                                      try { await api.post(`/sat/invoices/${it.id}/unlink`); toast.success("Desvinculada"); loadAll(); }
                                      catch { toast.error("Error al desvincular"); }
                                    }}
                                    data-testid={`sat-unlink-${it.id}`}
                                    className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1"
                                    style={{ color: "#fca5a5" }}
                                  >
                                    <Unlink className="w-3 h-3" /> Desvincular
                                  </button>
                                ) : it.match_status !== "not_applicable" && (
                                  <button
                                    onClick={() => setLinkModal(it)}
                                    data-testid={`sat-link-${it.id}`}
                                    className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1"
                                    style={{ color: "var(--cyan)" }}
                                  >
                                    <Link2 className="w-3 h-3" /> Vincular
                                  </button>
                                )}
                                <a
                                  href={`${api.defaults.baseURL}/sat/invoices/${it.id}/xml`}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                      const res = await api.get(`/sat/invoices/${it.id}/xml`, { responseType: "blob" });
                                      const url = URL.createObjectURL(res.data);
                                      const a = document.createElement("a");
                                      a.href = url; a.download = `${it.uuid}.xml`; a.click();
                                      URL.revokeObjectURL(url);
                                    } catch { toast.error("XML no disponible"); }
                                  }}
                                  data-testid={`sat-xml-${it.id}`}
                                  className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1"
                                >
                                  <FileDown className="w-3 h-3" /> XML
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showConfig && <ConfigModal onClose={() => setShowConfig(false)} onDone={() => { setShowConfig(false); loadAll(); }} />}
      {showSync && <SyncModal onClose={() => setShowSync(false)} onDone={() => { setShowSync(false); loadAll(); }} />}
      {linkModal && <LinkModal inv={linkModal} onClose={() => setLinkModal(null)} onDone={() => { setLinkModal(null); loadAll(); }} />}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center justify-between">
        <div className="label-mono text-[10px]">{label}</div>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="font-mono font-bold text-lg tabular-nums mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function SuggestionRow({ inv, onLinked }) {
  const [linking, setLinking] = useState(null);
  const link = async (c) => {
    setLinking(c.id);
    try {
      const payload = c.type === "exchange" ? { exchange_id: c.id } : { expense_id: c.id };
      await api.post(`/sat/invoices/${inv.id}/link`, payload);
      toast.success(c.type === "exchange" ? "Factura vinculada al cambio de efectivo" : "Factura vinculada al gasto");
      onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al vincular");
    } finally {
      setLinking(null);
    }
  };
  return (
    <div className="p-3 rounded-sm" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)" }} data-testid={`sat-suggestion-${inv.id}`}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="font-semibold text-slate-200">{inv.nombre_emisor}</span>
        <span className="font-mono text-slate-400">{inv.rfc_emisor}</span>
        <span className="font-mono font-bold" style={{ color: "#fbbf24" }}>{fmtMXN(inv.total)}</span>
        <span className="text-slate-500">{(inv.fecha || "").slice(0, 10)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {(inv.candidates || []).map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-[11px] flex-wrap">
            <span className="text-slate-300 flex-1 min-w-[180px] truncate">{c.vendor || "Sin proveedor"} · {c.description}</span>
            <span className="font-mono text-slate-400">{fmtMXN(c.amount)} · {c.expense_date}</span>
            <button
              onClick={() => link(c.id)}
              disabled={linking === c.id}
              data-testid={`sat-suggest-link-${inv.id}-${c.id}`}
              className="btn-primary px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1"
            >
              {linking === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Vincular
            </button>
          </div>
        ))}
        {(inv.candidates || []).length === 0 && (
          <div className="text-[11px] text-slate-500 italic">Los gastos candidatos ya fueron facturados.</div>
        )}
      </div>
    </div>
  );
}

function ConfigModal({ onClose, onDone }) {
  const [cer, setCer] = useState(null);
  const [key, setKey] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!cer || !key || !password) { toast.error("Completa los 3 campos"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("cer", cer);
      fd.append("key", key);
      fd.append("password", password);
      const { data } = await api.post("/sat/efirma", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(`e.firma validada con el SAT: ${data.rfc}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al validar la e.firma");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="sat-config-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// CONFIGURAR E.FIRMA (FIEL)</div>
            <h2 className="font-display font-bold text-lg text-slate-100 mt-1">Conexión con el SAT</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <label className="label-mono block mb-1">Certificado (.cer) *</label>
            <input type="file" accept=".cer" data-testid="sat-cer-input" onChange={(e) => setCer(e.target.files?.[0] || null)} className="input-cyber px-3 py-2 text-xs w-full" />
          </div>
          <div>
            <label className="label-mono block mb-1">Llave privada (.key) *</label>
            <input type="file" accept=".key" data-testid="sat-key-input" onChange={(e) => setKey(e.target.files?.[0] || null)} className="input-cyber px-3 py-2 text-xs w-full" />
          </div>
          <div>
            <label className="label-mono block mb-1">Contraseña de la llave privada *</label>
            <input type="password" required data-testid="sat-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="input-cyber px-3 py-2 text-sm w-full" autoComplete="off" />
          </div>
          <div className="px-3 py-2.5 rounded-sm text-[11px] text-slate-400" style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.2)" }}>
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" style={{ color: "var(--cyan)" }} />
            Se validará contra el SAT antes de guardar. La llave y contraseña se cifran con AES y nunca salen del servidor. Toda acción queda en el log de auditoría.
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="sat-config-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saving ? "Validando con SAT…" : "Validar y guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SyncModal({ onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [form, setForm] = useState({ date_start: monthStart, date_end: today });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sat/sync", form, { timeout: 60000 });
      toast.success("Solicitud enviada al SAT. Se descargará automáticamente cuando esté lista.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al solicitar la descarga");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-sm panel-strong" data-testid="sat-sync-modal">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="label-mono">// SOLICITAR DESCARGA AL SAT</div>
          <h2 className="font-display font-bold text-base text-slate-100 mt-1">Facturas recibidas</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1">Desde *</label>
              <input type="date" required data-testid="sat-date-start" value={form.date_start} onChange={(e) => setForm({ ...form, date_start: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <label className="label-mono block mb-1">Hasta *</label>
              <input type="date" required data-testid="sat-date-end" value={form.date_end} onChange={(e) => setForm({ ...form, date_end: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500">El SAT puede tardar de minutos a horas en preparar el paquete. El sistema lo revisa cada 15 min y lo importa solo.</p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="sat-sync-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" />}
            Solicitar
          </button>
        </div>
      </form>
    </div>
  );
}

function LinkModal({ inv, onClose, onDone }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/billing/pending-invoices")
      .then(({ data }) => setPending(data.items || []))
      .catch(() => toast.error("No se pudieron cargar los gastos pendientes"))
      .finally(() => setLoading(false));
  }, []);

  const link = async (p) => {
    setLinking(p.id);
    try {
      const payload = p.source === "cambio" ? { exchange_id: p.id } : { expense_id: p.id };
      await api.post(`/sat/invoices/${inv.id}/link`, payload);
      toast.success(p.source === "cambio" ? "Factura vinculada al cambio de efectivo" : "Factura vinculada al gasto");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al vincular");
    } finally {
      setLinking(null);
    }
  };

  const items = pending.filter((p) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (p.vendor || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg panel-strong" data-testid="sat-link-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// VINCULAR FACTURA A GASTO</div>
            <h2 className="font-display font-bold text-base text-slate-100 mt-1">{inv.nombre_emisor} · {fmtMXN(inv.total)}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <input
            data-testid="sat-link-search"
            placeholder="Buscar gasto pendiente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-cyber px-3 py-2 text-sm w-full"
          />
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {loading ? (
              <div className="text-center text-slate-400 text-xs py-6">Cargando…</div>
            ) : items.length === 0 ? (
              <div className="text-center text-slate-500 text-xs py-6">No hay gastos pendientes de factura.</div>
            ) : items.map((p) => {
              const exact = Math.abs(p.amount - inv.total) <= 0.01;
              return (
                <div key={p.id} className="flex items-center gap-2 p-2 rounded-sm text-xs" style={{ background: exact ? "rgba(34,197,94,0.07)" : "rgba(15,23,42,0.5)", border: `1px solid ${exact ? "rgba(34,197,94,0.3)" : "var(--border)"}` }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 truncate">
                      {p.source === "cambio" && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm mr-1.5" style={{ background: "#22d3ee18", color: "#22d3ee", border: "1px solid #22d3ee44" }}>CAJA</span>}
                      {p.vendor || "Sin proveedor"} · {p.description}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{fmtMXN(p.amount)} · {p.expense_date} {exact && <span style={{ color: "#22c55e" }}>· monto exacto</span>}</div>
                  </div>
                  <button
                    onClick={() => link(p)}
                    disabled={linking === p.id}
                    data-testid={`sat-link-pick-${p.id}`}
                    className="btn-primary px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-widest"
                  >
                    {linking === p.id ? "…" : "Vincular"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
